<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import UpsertTeamDialog from '@/components/dialogs/UpsertTeamDialog.vue';
import Link from '@/components/Link.vue';
import List from '@/components/List.vue';
import { OrganizationRole } from '@/enums/roles';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { useTeamStore } from '@/stores/team';
import type { Team } from '@/types/team';
import { onMounted, ref } from 'vue';

import { useRouter } from 'vue-router';

const router = useRouter();

const teamStore = useTeamStore();
const authStore = useAuthStore();
const globalStore = useGlobalStore();

const isUpsertTeamDialogOpen = ref(false);
const selectedTeam = ref<string>();
const isInitializing = ref(true);

onMounted(async () => {
    await teamStore.fetchTeamBasedOnRole();
    isInitializing.value = false;
});

const teamListHeaders = [
    { title: 'Naam', value: 'name', sortable: true },
    { title: 'Teamleider', value: 'teamleader', sortable: true },
    { title: 'Aantal medewerkers', value: (item: Team) => item.employees?.length ?? 0, sortable: true, key: 'amountOfEmployees' }
];

function currentEmployeeCanUseActions() {
    return authStore.isAuthorized([OrganizationRole.Admin], authStore.getCurrentOrganization()) && !authStore.isReadOnly();
}

function createTeam() {
    selectedTeam.value = undefined;
    isUpsertTeamDialogOpen.value = true;
}

function editTeam(id: string) {
    selectedTeam.value = id;
    isUpsertTeamDialogOpen.value = true;
}

function updateTeamInCache(team: Team) {
    const index = teamStore.teams.findIndex((t) => t._id === team._id);
    if (index !== -1) {
        teamStore.teams[index] = team;
    }
}

function addTeamToCache(newTeam: Team): void {
    teamStore.teams.push(newTeam);
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isInitializing && !globalStore.isLoading">
            <v-row class="align-center justify-space-between ma-0 mb-2" v-if="currentEmployeeCanUseActions()">
                <CustomButton @click="createTeam" text="Nieuw team aanmaken" />
            </v-row>

            <div v-if="teamStore.teams.length == 0">
                <p>Er zijn nog geen teams beschikbaar.</p>
            </div>

            <div v-else>
                <List
                    :isInitializing="isInitializing"
                    :headers="teamListHeaders"
                    :items="teamStore.teams"
                    :is-editable="currentEmployeeCanUseActions()"
                    :is-deletable="currentEmployeeCanUseActions()"
                    @click-table-row="(item) => router.push(`/team/${item._id}`)"
                    @click-table-row-edit="(item) => editTeam(item._id)"
                    @delete-confirmed="(item) => teamStore.deleteById(item._id)"
                    entity-name="Team"
                >
                    <template v-slot:item.name="{ item }">
                        <span>{{ item.name }}</span>
                    </template>
                    <template v-slot:item.teamleader="{ item }">
                        <Link
                            v-if="item.teamleader?.fullName"
                            :route-to="{ name: 'employee', params: { id: item.teamleader?._id } }"
                            icon="person"
                            :displayText="item.teamleader?.fullName"
                        />
                        <span v-else>Geen teamleider</span>
                    </template>
                </List>
            </div>
            <UpsertTeamDialog v-model="isUpsertTeamDialogOpen" :id="selectedTeam" @teamCreated="addTeamToCache" @teamUpdated="updateTeamInCache" />
        </div>
    </v-fade-transition>
</template>

<style scoped>
tr:nth-child(odd) {
    background-color: var(--vt-c-text-dark-2);
}
</style>
